import React from 'react';
import type {
  PlannedVsActualReportResponse,
  PlannedVsActualBoardRow,
} from '../../api/report.api';
import { formatHours, formatPercent, deviationClass } from '../../utils/planned-vs-actual';

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  data: PlannedVsActualReportResponse;
}

const PlannedVsActualBoardTable: React.FC<Props> = ({ data }) => {
  const { boardRows, grandTotal } = data;
  const gtDev = deviationClass(grandTotal.deviation, grandTotal.originalEstimate, grandTotal.timeSpent);

  return (
    <div className="rp-report-table__wrapper">
      <div className="rp-report-table__scroll">
        <table className="rp-report-table rp-pva-table">
          <thead>
            <tr>
              <th className="rp-pva-table__col-name">Board Name</th>
              <th className="rp-pva-table__col-count">Total Items</th>
              <th className="rp-pva-table__col-count">Total Assignees</th>
              <th className="rp-pva-table__col-hours">Original Estimate</th>
              <th className="rp-pva-table__col-hours">Time Spent</th>
              <th className="rp-pva-table__col-hours">Remaining</th>
              <th className="rp-pva-table__col-hours">Deviation</th>
              <th className="rp-pva-table__col-percent">Deviation %</th>
            </tr>
          </thead>
          <tbody>
            {boardRows.map((row) => (
              <BoardRow key={row.boardId} row={row} />
            ))}
            <tr className="rp-pva-table__summary-row rp-pva-table__grand-total-row">
              <td><strong>Grand Total</strong></td>
              <td className="rp-pva-table__col-count">{grandTotal.totalItems}</td>
              <td className="rp-pva-table__col-count">{grandTotal.totalAssignees}</td>
              <td className="rp-pva-table__col-hours">{formatHours(grandTotal.originalEstimate)}</td>
              <td className="rp-pva-table__col-hours">{formatHours(grandTotal.timeSpent)}</td>
              <td className="rp-pva-table__col-hours">{formatHours(grandTotal.remainingEstimate)}</td>
              <td className={`rp-pva-table__col-hours ${gtDev}`}>
                {formatHours(grandTotal.deviation)}
              </td>
              <td className={`rp-pva-table__col-percent ${gtDev}`}>
                {formatPercent(grandTotal.deviationPercent)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Report description */}
      <div className="rp-report-description">
        <div className="rp-report-description__title">How to read this report</div>
        <p className="rp-report-description__text">
          This view aggregates planned vs actual hours at the <strong>board level</strong>.
          Each row summarizes all items on a board, showing total estimates, time spent, and deviation.
        </p>
        <div className="rp-report-description__subtitle">Deviation color coding</div>
        <div className="rp-report-description__legend">
          <span className="rp-report-description__swatch" style={{ background: '#d83a52' }} />
          <span>Over budget &mdash; Time Spent exceeds the Original Estimate</span>
        </div>
        <div className="rp-report-description__legend">
          <span className="rp-report-description__swatch" style={{ background: '#00a25b' }} />
          <span>On track &mdash; Time Spent is within 20% of the Original Estimate</span>
        </div>
        <div className="rp-report-description__legend">
          <span className="rp-report-description__swatch" style={{ background: '#fdab3d' }} />
          <span>Under-logged &mdash; significant gap between estimate and actual hours</span>
        </div>
        <div className="rp-report-description__legend">
          <span className="rp-report-description__swatch" style={{ background: '#676879' }} />
          <span>No data &mdash; neither estimate nor time spent recorded</span>
        </div>
        <div className="rp-report-description__subtitle">Key details</div>
        <ul className="rp-report-description__list">
          <li>
            <strong>Total Items</strong> counts items with estimates or worklogs on this board.
          </li>
          <li>
            <strong>Total Assignees</strong> is the distinct count of people assigned across all items.
          </li>
          <li>
            Switch to the <strong>&ldquo;By Item&rdquo;</strong> view to drill into individual item details.
          </li>
        </ul>
      </div>
    </div>
  );
};

// ─── Board row ───────────────────────────────────────────────────────────────

const BoardRow: React.FC<{ row: PlannedVsActualBoardRow }> = ({ row }) => {
  const dev = deviationClass(row.deviation, row.originalEstimate, row.timeSpent);
  return (
    <tr className="rp-report-table__user-row">
      <td className="rp-pva-table__col-name">{row.boardName}</td>
      <td className="rp-pva-table__col-count">{row.totalItems}</td>
      <td className="rp-pva-table__col-count">{row.totalAssignees}</td>
      <td className="rp-pva-table__col-hours">{formatHours(row.originalEstimate)}</td>
      <td className="rp-pva-table__col-hours">{formatHours(row.timeSpent)}</td>
      <td className="rp-pva-table__col-hours">{formatHours(row.remainingEstimate)}</td>
      <td className={`rp-pva-table__col-hours ${dev}`}>
        {formatHours(row.deviation)}
      </td>
      <td className={`rp-pva-table__col-percent ${dev}`}>
        {formatPercent(row.deviationPercent)}
      </td>
    </tr>
  );
};

export default PlannedVsActualBoardTable;
